import { watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAdminStore } from '@app/stores';
import { AdminRouteNames } from '@app/router/route-names';

/**
 * Blocks admin-only pages for employees who can't manage others: the menu hides
 * them, and this sends direct navigations (deep links, bottom-nav taps on stale
 * builds) back home. clientRole is read from the session token before the
 * router-view renders, so checking at setup is safe; the watch covers role
 * changes without a remount.
 */
export function useAdminOnlyPage() {
  const router = useRouter();
  const adminStore = useAdminStore();

  const check = () => {
    if (!adminStore.getCanManageOthers) {
      router.replace({ name: AdminRouteNames.Home });
    }
  };
  check();
  watch(() => adminStore.getCanManageOthers, check);
}

/**
 * Same idea, for pages that show one employee: managers may open anyone, a regular
 * employee only their own record (they manage their own services and working hours
 * there). Anything else goes back home.
 */
export function useSelfOrManagerPage(targetUserId: () => string) {
  const router = useRouter();
  const adminStore = useAdminStore();

  const check = () => {
    if (adminStore.getCanManageOthers) return;
    // The id can be empty for a tick while the route params settle.
    const target = targetUserId();
    if (target && target !== adminStore.getUserId) {
      router.replace({ name: AdminRouteNames.Home });
    }
  };
  check();
  watch([() => adminStore.getCanManageOthers, () => adminStore.getUserId, targetUserId], check);
}
